import type { MemberFilters } from "@domains/reviews/shared/reviewee-filters/memberFilters.ts";
import { TENURE_BUCKET_KEYS, type TenureBucketKey } from "@domains/reviews/shared/reviewee-filters/tenureBuckets.ts";
import { toIsReportingManager, TrackOptions } from "@domains/reviews/shared/reviewee-filters/trackOptions.ts";
import { type PeriodSelection, ReviewHistoryFilters } from "#shared/periodSelection.ts";
import { isUnsetGroupKey } from "#shared/reviewScoresContract.ts";
type ComparisonGroupFilters = MemberFilters | { tenure: TenureBucketKey } | PeriodSelection;

type ComparisonScope = MemberFilters & PeriodSelection;

function toTenureBand(groupKey: string): ComparisonGroupFilters | undefined {
    const band = TENURE_BUCKET_KEYS.find(key => key === groupKey);

    return band && { tenure: band };
}

export const ComparisonDimensions = ["cycle", "timeRange", "manager", "reportingLine", "segment", "function", "gender", "workLocation", "roleLevel", "jobTitle", "tenure", "track"] as const;
export type ComparisonDimension = (typeof ComparisonDimensions)[number];

const SCOPE_FIELDS_BY_DIMENSION = {
    cycle: ["reviewCycleIds"],
    timeRange: ["reviewHistoryFilter"],
    segment: ["segmentIds"],
    manager: ["managerIds"],
    reportingLine: ["reportingLineIds"],
    function: ["departments"],
    gender: ["genders"],
    workLocation: ["workLocations"],
    roleLevel: ["jobLevels"],
    jobTitle: ["jobTitles"],
    tenure: ["hireDateMin", "hireDateMax"],
    track: ["isReportingManager"]
} as const satisfies Record<ComparisonDimension, readonly (keyof ComparisonScope)[]>;

function toDimensionKey(selection: ComparisonScope, dimension: ComparisonDimension): string {
    return JSON.stringify(
        SCOPE_FIELDS_BY_DIMENSION[dimension].map(field => {
            const value = selection[field];

            return Array.isArray(value) ? value.toSorted() : (value ?? null);
        })
    );
}

export function differsOnDimension(selections: ComparisonScope[], dimension: ComparisonDimension): boolean {
    return new Set(selections.map(selection => toDimensionKey(selection, dimension))).size > 1;
}

export function toComparisonGroupFilters(dimension: ComparisonDimension, groupKey: string): ComparisonGroupFilters | undefined {
    if (isUnsetGroupKey(groupKey)) {
        return undefined;
    }

    switch (dimension) {
        case "cycle":
            return { reviewCycleIds: [groupKey], reviewHistoryFilter: undefined, from: undefined, to: undefined };
        case "timeRange": {
            const preset = ReviewHistoryFilters.find(candidate => candidate === groupKey);

            return preset && { reviewHistoryFilter: preset, reviewCycleIds: undefined, from: undefined, to: undefined };
        }
        case "segment":
            return { segmentIds: [groupKey] };
        case "manager":
            return { managerIds: [groupKey] };
        case "reportingLine":
            return { reportingLineIds: [groupKey] };
        case "function":
            return { departments: [groupKey] };
        case "gender":
            return { genders: [groupKey] };
        case "workLocation":
            return { workLocations: [groupKey] };
        case "roleLevel":
            return { jobLevels: [groupKey] };
        case "jobTitle":
            return { jobTitles: [groupKey] };
        case "tenure":
            return toTenureBand(groupKey);
        case "track": {
            const option = TrackOptions.find(candidate => candidate === groupKey);

            return option && { isReportingManager: toIsReportingManager(option) };
        }
    }
}
